import { FC, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import useSearchWithQuery from "@/hooks/useSearchWithQuery";
import ModalSearchChildContainer from "@/components/ModalSearchChildContainer";
import { ArchitectureSearchQueryParameter } from "@/types/ArchitectureSearchQueryParameter";
import {
  BuildingTypeResponse,
  ProjectConstructionType,
} from "@/web-client/api";
import ConstructionTypeSelection from "@/components/ConstructionTypeSelection";
import SearchConditionTile from "@/components/SearchConditionTile";
import formatConstructionType from "@/utils/formatConstructionType";
import StructureTypeSelection from "@/components/StructureTypeSelection";
import FloorSpaceSelection from "@/components/FloorSpaceSelection";
import SearchConditionsTile from "@/components/SearchConditionsTile";
import formatRange from "@/utils/formatRange";
import BuildingMaterialColorSelection from "@/components/BuildingMaterialColorSelection";
import Colors from "@/consts/Colors";
import { StructureTypes } from "@/config/StrctureTypes";

interface Props {
  query: ArchitectureSearchQueryParameter;
  closeHandler: () => void;
  buildingTypes: BuildingTypeResponse[];
}

// 入力フォームの値をすべてここに書いてください
const initValues: ArchitectureSearchQueryParameter = {
  keyword: "",
  structure_type_id: "",
  construction_type: "",
  max_floor_space: "",
  min_floor_space: "",
  color_type_ids: "",
};

export type ArchitectureSearchSubmitParameter = Omit<
  ArchitectureSearchQueryParameter,
  "color_type_ids"
> & {
  color_type_ids?: string[];
};

const ModalSearchArchitectureChild: FC<Props> = ({ query, closeHandler }) => {
  const defaultValues = useMemo(() => {
    return {
      ...initValues,
      ...query,
    };
  }, [query]);

  const { register, handleSubmit, reset, getValues, setValue, control } =
    useForm<ArchitectureSearchQueryParameter>({
      defaultValues,
    });

  const [selectionId, setSelectionId] = useState<
    keyof ArchitectureSearchQueryParameter | "floor_space"
  >(null);

  const search = useSearchWithQuery<ArchitectureSearchQueryParameter>();

  const onSubmit = (data: ArchitectureSearchQueryParameter) => {
    search(data);
    closeHandler();
  };

  const backHandler = () => setSelectionId(null);

  const maxFloorSpaceValue = getValues("max_floor_space");
  const minFloorSpaceValue = getValues("min_floor_space");
  const floorSpaceConditionLabel = useMemo(
    () => formatRange("㎡", minFloorSpaceValue, maxFloorSpaceValue),
    [maxFloorSpaceValue, minFloorSpaceValue],
  );

  return (
    <ModalSearchChildContainer<ArchitectureSearchQueryParameter>
      handleSubmit={handleSubmit}
      register={register}
      onSubmit={onSubmit}
      onClear={() => reset(initValues)}
      modalElements={
        <>
          {selectionId === "color_type_ids" && (
            <BuildingMaterialColorSelection
              defaultValue={getValues()}
              onUpdate={({ color_type_ids }) => {
                setValue("color_type_ids", color_type_ids);
              }}
              onBack={backHandler}
            />
          )}

          {selectionId === "construction_type" && (
            <ConstructionTypeSelection
              defaultValue={getValues()}
              onUpdate={({ construction_type }) =>
                setValue("construction_type", construction_type)
              }
              onBack={backHandler}
            />
          )}

          {selectionId === "structure_type_id" && (
            <StructureTypeSelection
              defaultValue={getValues()}
              onUpdate={({ structure_type_id }) =>
                setValue("structure_type_id", structure_type_id)
              }
              onBack={backHandler}
            />
          )}

          {selectionId === "floor_space" && (
            <FloorSpaceSelection
              defaultValue={getValues()}
              onUpdate={({ min_floor_space, max_floor_space }) => {
                setValue("min_floor_space", min_floor_space);
                setValue("max_floor_space", max_floor_space);
              }}
              onBack={backHandler}
            />
          )}
        </>
      }
      hiddenModal={selectionId == null}
    >
      <ul className="BorderList">
        {
          // 建築材料が選択されていなければ、表示はしない
          query.material_id && (
            <li className="flex">
              <SearchConditionTile
                control={control}
                name="color_type_ids"
                label={"建築材料の色"}
                formatCondition={(value) => {
                  const ids = value.split(",");
                  const selectedColors = [];
                  ids.map((id) => {
                    const selectedColor = Colors.find(
                      (c) => c.id.toString() === id,
                    );

                    if (selectedColor) selectedColors.push(selectedColor.name);
                  });

                  return selectedColors.join(", ");
                }}
                onTap={() => setSelectionId("color_type_ids")}
              />
            </li>
          )
        }

        <li className="flex">
          <SearchConditionTile
            control={control}
            name="construction_type"
            label={"工事種別"}
            formatCondition={(value) =>
              value
                ? formatConstructionType(
                    Number(value) as ProjectConstructionType,
                  )
                : ""
            }
            onTap={() => setSelectionId("construction_type")}
          />
        </li>

        <li className="flex">
          <SearchConditionTile
            control={control}
            name="structure_type_id"
            label={"構造"}
            formatCondition={(value) =>
              StructureTypes.find((s) => s.id.toString() === value)?.label
            }
            onTap={() => setSelectionId("structure_type_id")}
          />
        </li>

        <li className="flex">
          <SearchConditionsTile
            label={"面積"}
            conditionLabel={<>{floorSpaceConditionLabel}</>}
            onTap={() => setSelectionId("floor_space")}
          />
        </li>
      </ul>
    </ModalSearchChildContainer>
  );
};
export default ModalSearchArchitectureChild;
